import React from "react";
import ReactDOM from "react-dom";
import App from "./App";
import "react-datepicker/dist/react-datepicker.css";
import "./font.css";
import "./styles/embedded.css";
import { StoreProvider } from "./stores/setup/store-context";
import { createStore } from "./stores/setup/create-store";
import { ChakraProvider } from "@chakra-ui/react";
import myTheme from "./styles/theme";
import { isEmbeddedMode, initEmbeddedAuth } from "./util/embedded";

import "./i18n";

import * as Sentry from "@sentry/react";
import { getConfig, getParentAppOrigins } from "config/app.config";
import { ConfigKey } from "enum/ConfigKey";

import posthog from "posthog-js";
import { PostHogProvider } from "posthog-js/react";
import { register } from "serviceWorkerRegistration";

Sentry.init({
  dsn: getConfig(ConfigKey.REACT_APP_SENTRY_DSN),
  ignoreErrors: [/^(?!.*500).*$/],
  integrations: [
    Sentry.browserTracingIntegration(),
    Sentry.replayIntegration(),
  ],
  // Tracing
  tracesSampleRate: 1.0, //  Capture 100% of the transactions
  // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
  tracePropagationTargets: [
    /^https:\/\/i4cast\.i4sea\.com\//, // Frontend URL
    /^https:\/\/i4cast-backend\.i4sea\.com\.br\/v1\//, // Backend URL
  ],
  // Session Replay
  replaysSessionSampleRate: 0.1, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
  replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
});

// Posthog - Product Analytics
posthog.init(getConfig(ConfigKey.REACT_APP_PUBLIC_POSTHOG_KEY), {
  api_host: getConfig(ConfigKey.REACT_APP_PUBLIC_POSTHOG_HOST),
  persistence: "localStorage",
  capture_pageview: false,
  autocapture: false,
  disable_session_recording: true,
  loaded: (posthog) => {
    console.log("PostHog loaded successfully");
  },
  debug: false,
});

const rootStore = createStore();

register();

// Apply embedded-mode class to body if running in embedded mode
// and initialize postMessage auth listener
if (isEmbeddedMode()) {
  document.body.classList.add('embedded-mode');

  // Initialize postMessage authentication for receiving JWT from parent app
  // Parent origins allowed to exchange auth/navigation messages with this iframe.
  // REACT_APP_PARENT_APP_URL accepts a comma-separated list so multiple parent
  // deployments (e.g. prod + Netlify preview) can be authorized per environment.
  const allowedOrigins = [
    ...getParentAppOrigins(),
    'http://localhost:3000', // dev
  ];

  initEmbeddedAuth(allowedOrigins);

  // Reset all stores when parent app triggers logout
  // Guard against duplicate listeners on HMR/Fast Refresh re-evaluation
  const embeddedLogoutHandler = () => {
    const { dataStores, uiStores } = rootStore;
    uiStores.menuViewStore.reset();
    dataStores.authStore.reset();
    dataStores.resetDataStores();
    dataStores.securityStore.clearTokenSet();
  };

  const prev = (window as any).__embeddedLogoutHandler as EventListener | undefined;
  if (prev) {
    window.removeEventListener('embedded-logout', prev);
  }
  (window as any).__embeddedLogoutHandler = embeddedLogoutHandler;
  window.addEventListener('embedded-logout', embeddedLogoutHandler);

  // Set userId in the store when AUTH_TOKEN arrives from parent
  const embeddedAuthTokenHandler = ((event: CustomEvent) => {
    if (event.detail?.userId) {
      rootStore.dataStores.authStore.setUserId(event.detail.userId);
    }
  }) as EventListener;

  const prevAuth = (window as any).__embeddedAuthTokenHandler as EventListener | undefined;
  if (prevAuth) {
    window.removeEventListener('embedded-auth-token', prevAuth);
  }
  (window as any).__embeddedAuthTokenHandler = embeddedAuthTokenHandler;
  window.addEventListener('embedded-auth-token', embeddedAuthTokenHandler);
}

ReactDOM.render(
  <ChakraProvider theme={myTheme}>
    <StoreProvider value={rootStore}>
      <PostHogProvider client={posthog}>
        <App />
      </PostHogProvider>
    </StoreProvider>
  </ChakraProvider>,
  document.getElementById("root")
);
